/**
 * components/button.tsx — the design's `.t-btn`.
 * Variants: default | primary | accent | success | danger | ghost | outline.
 * Sizes: md (32) | sm (26) | xs (22) | lg (38). Supports leading/trailing icon,
 * icon-only, and full-width (block). Hover handled on web.
 */
import React, { useState } from 'react';
import { Pressable, Platform, StyleProp, ViewStyle } from 'react-native';
import { LightColors as C, Radii as R, FontSize as FS, FontWeight as FW } from '../theme/theme';

const isWeb = Platform.OS === 'web';
import { Txt } from './ui';
import Icon, { IconName } from './icon';

export type ButtonVariant = 'default' | 'primary' | 'accent' | 'success' | 'danger' | 'ghost' | 'outline';
export type ButtonSize = 'md' | 'sm' | 'xs' | 'lg';

interface VariantStyle {
  bg: string;
  border: string;
  fg: string;
  hoverBg: string;
}

const VARIANTS: Record<ButtonVariant, VariantStyle> = {
  default: { bg: C.card, border: C.line, fg: C.ink, hoverBg: C.paper2 },
  primary: { bg: C.ink, border: C.ink, fg: C.paper, hoverBg: C.accentInk },
  accent: { bg: C.accent, border: C.accent, fg: '#FFFFFF', hoverBg: C.accentInk },
  success: { bg: C.success, border: C.success, fg: '#FFFFFF', hoverBg: C.ink },
  danger: { bg: C.danger, border: C.danger, fg: '#FFFFFF', hoverBg: C.ink },
  ghost: { bg: 'transparent', border: 'transparent', fg: C.ink2, hoverBg: C.paper2 },
  outline: { bg: 'transparent', border: C.line, fg: C.ink, hoverBg: C.paper2 },
};

const SIZES: Record<ButtonSize, { h: number; px: number; fs: number; icon: number }> = {
  md: { h: 32, px: 14, fs: FS.md, icon: 15 },
  sm: { h: 26, px: 10, fs: FS.sm, icon: 13 },
  xs: { h: 22, px: 8, fs: FS.xs, icon: 12 },
  lg: { h: 38, px: 18, fs: FS.lg, icon: 17 },
};

export interface ButtonProps {
  label?: string;
  children?: React.ReactNode;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
  iconOnly?: boolean;
  block?: boolean;
  disabled?: boolean;
  /** Stronger icon stroke for chunky check/plus glyphs (matches design 2.2–2.6). */
  iconStroke?: number;
  style?: StyleProp<ViewStyle>;
}

const Button: React.FC<ButtonProps> = ({
  label,
  children,
  onPress,
  variant = 'default',
  size = 'md',
  icon,
  iconRight,
  iconOnly = false,
  block = false,
  disabled = false,
  iconStroke = 2,
  style,
}) => {
  const [hover, setHover] = useState(false);
  const v = VARIANTS[variant];
  const s = SIZES[size];
  const text = label ?? (typeof children === 'string' ? children : undefined);
  // Premium hover: a subtle lift + shadow on solid buttons (web). Applies the
  // same feel to every solid button (Save draft, Save & activate, …).
  const lift = isWeb && hover && !disabled && variant !== 'ghost' && variant !== 'outline';

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      disabled={disabled}
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          height: s.h,
          paddingHorizontal: iconOnly ? 0 : s.px,
          width: iconOnly ? s.h : block ? '100%' : undefined,
          borderRadius: R.r2,
          borderWidth: 1,
          borderColor: v.border,
          backgroundColor: hover && !disabled ? v.hoverBg : v.bg,
          opacity: disabled ? 0.5 : 1,
          ...(isWeb ? ({ transitionProperty: 'transform, box-shadow, background-color', transitionDuration: '140ms' } as object) : {}),
          ...(lift ? ({ transform: [{ translateY: -1 }], boxShadow: '0 6px 16px -4px rgba(16,24,40,0.28)' } as object) : {}),
        },
        style,
      ]}
    >
      {icon && <Icon name={icon} size={s.icon} color={v.fg} strokeWidth={iconStroke} />}
      {text !== undefined ? (
        <Txt size={s.fs} weight={FW.medium} color={v.fg} numberOfLines={1}>
          {text}
        </Txt>
      ) : (
        typeof children !== 'string' && children
      )}
      {iconRight && <Icon name={iconRight} size={s.icon} color={v.fg} strokeWidth={iconStroke} />}
    </Pressable>
  );
};

export default Button;
